import React, { useState, useRef, useEffect } from "react";
import { usePopper } from "react-popper";
import "../CSS/Header.css";
import logo from "../Images/medwell-logo.png";
import { Link } from "react-router-dom";

const SOLUTIONS = [
  { label: "Payer", href: "/payer" },
  { label: "Workers' Compensation", href: "/workers-comp" },
  { label: "Revenue Cycle Management", href: "/revenue-cycle" },
  { label: "Life Sciences", href: "/life-sciences" },
];

const SERVICES = [
  { label: "Coding", href: "/coding-services" },
  { label: "Utilization Management", href: "/utilization-management" },
  { label: "Bill Review", href: "/bill-review" },
  { label: "Patient Care", href: "/patient-care" },
  { label: "Absence & Disability", href: "/absence-management" },
  { label: "U.S. Nursing Jobs", href: "/nursing-jobs" },
];

const Header = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const navItemRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(navItemRef.current, popperElement, {
    placement: "bottom",
    modifiers: [
      { name: "offset", options: { offset: [0, 20] } },
      { name: "preventOverflow", options: { boundary: "clippingParents" } },
    ],
  });

  // Services dropdown state
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [servicesSelected, setServicesSelected] = useState(0);
  const servicesNavItemRef = useRef<HTMLButtonElement | null>(null);
  const servicesDropdownRef = useRef<HTMLDivElement | null>(null);
  const [servicesPopperElement, setServicesPopperElement] = useState<HTMLDivElement | null>(null);
  const servicesPopper = usePopper(servicesNavItemRef.current, servicesPopperElement, {
    placement: "bottom",
    modifiers: [
      { name: "offset", options: { offset: [0, 20] } },
      { name: "preventOverflow", options: { boundary: "clippingParents" } },
    ],
  });

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        navItemRef.current &&
        !navItemRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target as Node) &&
        servicesNavItemRef.current &&
        !servicesNavItemRef.current.contains(event.target as Node)
      ) {
        setServicesDropdownOpen(false);
      }
    };
    if (dropdownOpen || servicesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen, servicesDropdownOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileSolutionsOpen(false);
    setMobileServicesOpen(false);
  };

  return (
    <>
      <header className="header">
        <div className="header-left">
          <Link to="/">
            <img src={logo} alt="Medwell360 Logo" className="header-logo" />
          </Link>
        </div>

        {/* Mobile toggle button (Hamburger vs Close) */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          {mobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </>
          )}
        </button>

        {/* Desktop navigation */}
        <nav className="header-nav desktop-nav">
          <div className="dropdown-nav-item">
            <button
              className={`dropbtn nav-solutions${dropdownOpen ? " nav-solutions-active" : ""}`}
              onClick={() => setDropdownOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              ref={navItemRef}
            >
              Our Solutions <span className="arrow">▼</span>
            </button>
            <div className={`nav-underline${dropdownOpen ? " nav-underline-active" : ""}`}></div>
            {dropdownOpen && (
              <div
                className="dropdown-list-wide"
                ref={(node) => {
                  dropdownRef.current = node;
                  setPopperElement(node);
                }}
                style={styles.popper}
                {...attributes.popper}
              >
                <div className="dropdown-grid">
                  {SOLUTIONS.map((option, idx) => (
                    <Link
                      key={option.label}
                      to={option.href}
                      className={`dropdown-grid-option${selected === idx ? " selected" : ""}`}
                      onClick={() => {
                        setSelected(idx);
                        setDropdownOpen(false);
                      }}
                    >
                      <span>{option.label}</span>
                      <span className="dropdown-arrow">&#8250;</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="dropdown-nav-item">
            <button
              className={`dropbtn nav-services${servicesDropdownOpen ? " nav-services-active" : ""}`}
              onClick={() => setServicesDropdownOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={servicesDropdownOpen}
              ref={servicesNavItemRef}
            >
              Our Services <span className="arrow">▼</span>
            </button>
            <div className={`nav-underline${servicesDropdownOpen ? " nav-underline-active" : ""}`}></div>
            {servicesDropdownOpen && (
              <div
                className="dropdown-list-wide"
                ref={(node) => {
                  servicesDropdownRef.current = node;
                  setServicesPopperElement(node);
                }}
                style={servicesPopper.styles.popper}
                {...servicesPopper.attributes.popper}
              >
                <div className="dropdown-grid">
                  {SERVICES.map((option, idx) => (
                    <Link
                      key={option.label}
                      to={option.href}
                      className={`dropdown-grid-option${servicesSelected === idx ? " selected" : ""}`}
                      onClick={() => {
                        setServicesSelected(idx);
                        setServicesDropdownOpen(false);
                      }}
                    >
                      <span>{option.label}</span>
                      <span className="dropdown-arrow">&#8250;</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="nav-item"><Link to="/" className="nav-link">Who We Are</Link></div>
          <div className="nav-item"><Link to="/careers" className="nav-link">Careers</Link></div>
          <div className="nav-item"><Link to="/contact-us" className="nav-link">Contact Us</Link></div>
        </nav>
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
      )}

      {/* Mobile menu drawer */}
      <div className={`mobile-menu${mobileMenuOpen ? " mobile-menu-open" : ""}`}>
        <nav className="mobile-menu-nav">
          {/* Our Solutions - Accordion */}
          <div className="mobile-nav-item">
            <button
              className="mobile-nav-button"
              onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
              aria-expanded={mobileSolutionsOpen}
            >
              <span>Our Solutions</span>
              <svg
                className={`mobile-chevron${mobileSolutionsOpen ? " mobile-chevron-open" : ""}`}
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#29939b"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {mobileSolutionsOpen && (
              <div className="mobile-submenu">
                {SOLUTIONS.map((option) => (
                  <Link
                    key={option.label}
                    to={option.href}
                    className="mobile-submenu-link"
                    onClick={closeMobileMenu}
                  >
                    {option.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Our Services - Accordion */}
          <div className="mobile-nav-item">
            <button
              className="mobile-nav-button"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              aria-expanded={mobileServicesOpen}
            >
              <span>Our Services</span>
              <svg
                className={`mobile-chevron${mobileServicesOpen ? " mobile-chevron-open" : ""}`}
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#29939b"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            {mobileServicesOpen && (
              <div className="mobile-submenu">
                {SERVICES.map((option) => (
                  <Link
                    key={option.label}
                    to={option.href}
                    className="mobile-submenu-link"
                    onClick={closeMobileMenu}
                  >
                    {option.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Simple nav links - Link keeps client-side routing so app does not unmount on mobile */}
          <div className="mobile-nav-item">
            <Link to="/" className="mobile-nav-link" onClick={closeMobileMenu}>
              Who We Are
            </Link>
          </div>
          <div className="mobile-nav-item">
            <Link to="/careers" className="mobile-nav-link" onClick={closeMobileMenu}>
              Careers
            </Link>
          </div>
          <div className="mobile-nav-item">
            <Link to="/contact-us" className="mobile-nav-link" onClick={closeMobileMenu}>
              Contact Us
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;